import { createAutoAnimate } from "@formkit/auto-animate/solid";
import { A, useNavigate, useLocation } from "@solidjs/router";
import { Accessor, JSX, Match, Setter, Show, Switch, createSignal } from "solid-js";
import {
  giftCardIcon,
  logoutIcon,
  profileIcon,
  rightArrowWhite,
  supportIcon,
} from "~/assets/assets";
import { Mode } from "~/types";
import { PhosphorIcon } from "~/widgets/icons";
import { AboutGiftCard } from "./about_gift_card";
import { useModal } from "./modal";
import { openIntercom } from "~/utils/third_party/intercom";
import { BusinessMenu } from "./business_menu";
import { classNames } from "~/utils/etc";
import { Spacer } from "~/widgets/spacer";
import { DividerV2 } from "~/widgets/divider";
import { clientRepo } from "~/server/apis/client_repo";
import {
  profileSvgIcon,
  giftCardSvgIcon,
  helpAndSupportSvgIcon,
  termsAndConditionsSvgIcon,
  logoutSvgIcon,
  profileSvgIconDark,
  giftCardSvgIconDark,
  termsAndConditionsSvgIconDark,
  helpAndSupportSvgIconDark,
  logoutSvgIconDark,
} from "../assets/header_menu_svg_icons";
import { clientIdConfig, getPartnerFaqs } from "~/data/partner_config";
import PartnerFAQs from "./partner_components/faqs";
import { RnrPortalsJson } from "~/server/types/client";
import { useOutsideClick } from "~/utils/hooks/use_outside_click";
import { isFreshLogin } from "./header/header_webstore";

export function HeaderMenu(props: {
  isLoggedIn: boolean;
  showBusinessMenu?: boolean;
  mode: Mode;
  clientId?: () => string;
  showMyGiftCards?: Accessor<boolean | undefined>;
  rnrPortalsJson?: RnrPortalsJson;
  loginToWebstore: (callback: () => void) => Promise<void>;
  isDropdownOpen: Accessor<boolean>;
  setIsDropdownOpen: Setter<boolean>;
  headerButtonRef?: Accessor<HTMLDivElement | undefined>;
}) {
  const [parent] = createAutoAnimate();
  const [menuRef, setMenuRef] = createSignal<HTMLDivElement>();

  useOutsideClick(
    menuRef,
    () => {
      props.setIsDropdownOpen(false);
    },
    props.headerButtonRef
  );


  return (
    <div class="relative">
      <div
        style={{
          "box-shadow": "0px 3.6px 5.4px 0px rgba(219, 221, 222, 0.20)",
        }}
        id="header-menu"
        class="shadow hidden h-10 w-10 cursor-pointer items-center justify-center gap-[9px] rounded-[34.20px] border border-neutral-200 bg-baseTertiaryLight lg:inline-flex"
        tabIndex="0"
        onClick={(e) => {
          e.stopPropagation();
          props.setIsDropdownOpen(!props.isDropdownOpen());
        }}
      >
        <img src={profileIcon} class="h-6" />
      </div>

      <div
        class={classNames(
          `fixed left-0 mt-2 flex w-full flex-col bg-baseTertiaryLight lg:top-[35px]`,
          `md:absolute md:-left-[150px] md:top-[10px] md:w-48 `
        )}
        classList={{
          "md:rounded-xl md:border md:border-neutral-200": props.isDropdownOpen(),
          "top-[100px]": props.clientId?.() === clientIdConfig.axisClientId,
          "top-[3.5rem]":
            props.clientId?.() === clientIdConfig.tdClientId ||
            props.clientId?.() === clientIdConfig.bajajClientId,
          "top-11":
            !(props.clientId?.() === clientIdConfig.axisClientId) &&
            !(props.clientId?.() === clientIdConfig.tdClientId),
        }}
        ref={setMenuRef}
      >
        <div ref={parent}>
          <Show when={props.isDropdownOpen()}>
            <DropdownMenu {...props} />
          </Show>
        </div>
      </div>
    </div>
  );

  function DropdownMenu(menuProps: { setIsDropdownOpen: Setter<boolean> } & Omit<typeof props, 'isDropdownOpen' | 'setIsDropdownOpen'>) {
    const navigate = useNavigate();
    const location = useLocation();

    const { setIsModalOn, updateModalContentGetter } = useModal()!;

    const isLoggedIn = isFreshLogin() ?? menuProps.isLoggedIn;

    const handleLogout = async () => {
      await clientRepo.logout();
      navigate("/logout");
    };

    const handleNavigation = (path: string) => {
      menuProps.setIsDropdownOpen(false);
      if (location.pathname === path) {
        // No navigation needed if already on the same path
      } else {
        navigate(path);
      }
    };

    return (
      <div
        class="flex flex-col p-2"
        classList={{
          "py-0 px-4":
            menuProps.clientId?.() === clientIdConfig.tdClientId ||
            menuProps.clientId?.() === clientIdConfig.bajajClientId,
        }}
      >
        <Show when={menuProps.showBusinessMenu}>
          <div class="md:hidden">
            <div class="truncate-[0.44px] pl-2 text-[11px] font-medium text-textNormal">
              FOR BUSINESS
            </div>
            <Spacer height={8} />
            <BusinessMenu />
            <Spacer height={8} />
            <DividerV2 class="bg-baseTertiaryMedium" />
            <Spacer height={8} />
          </div>
        </Show>

        <Switch>
          <Match when={!isLoggedIn}>
            <Show when={menuProps.mode != "rnr"}>
              <HeaderDropdownMenuItem
                title="Log in / Sign up"
                icon="user"
                iconUrl={profileIcon}
                iconBuilder={profileSvgIcon}
                darkIconBuilder={profileSvgIconDark}
                onClick={() => {
                  props.loginToWebstore(() => { });
                  menuProps.setIsDropdownOpen(false); // Close after logging in
                }}
              />
            </Show>
          </Match>
          <Match when={true}>
            <HeaderDropdownMenuItem
              title="Profile "
              icon="user"
              iconUrl={profileIcon}
              iconBuilder={profileSvgIcon}
              darkIconBuilder={profileSvgIconDark}
              onClick={() => handleNavigation("/profile")}
              clientId={() => menuProps.clientId?.()!}
            />
            <Show when={menuProps.showMyGiftCards?.()}>
              <HeaderDropdownMenuItem
                title="My gift cards"
                icon="gift"
                iconUrl={giftCardIcon}
                iconBuilder={giftCardSvgIcon}
                darkIconBuilder={giftCardSvgIconDark}
                onClick={() => handleNavigation("/profile")}
                clientId={() => menuProps.clientId?.()!}
              />
            </Show>
          </Match>
        </Switch>
        <Show when={menuProps.mode != "rnr"}>
          <div class="lg:hidden">
            <HeaderDropdownMenuItem
              title="What’s a gift card?"
              icon="question"
              iconBuilder={giftCardSvgIcon}
              darkIconBuilder={giftCardSvgIconDark}
              onClick={() => {
                updateModalContentGetter(() => {
                  return <AboutGiftCard />;
                });
                setIsModalOn(true);
                menuProps.setIsDropdownOpen(false);
              }}
            />
          </div>
        </Show>
        <Show
          when={
            menuProps.rnrPortalsJson?.getPartnerPortalTncUrl ||
            menuProps.clientId?.() === clientIdConfig.axisClientId
          }
        >
          <A
            href={menuProps.rnrPortalsJson?.getPartnerPortalTncUrl ?? ""}
            class="select-none"
            onClick={() => menuProps.setIsDropdownOpen(false)}
          >
            <HeaderDropdownMenuItem
              title="Terms & conditions"
              icon="headset"
              iconUrl={supportIcon}
              iconBuilder={termsAndConditionsSvgIcon}
              darkIconBuilder={termsAndConditionsSvgIconDark}
              onClick={() => { }}
              clientId={() => menuProps.clientId?.()!}
            />
          </A>
        </Show>
        <Show when={getPartnerFaqs(menuProps.clientId?.())}>
          <HeaderDropdownMenuItem
            title="FAQs"
            icon="headset"
            iconUrl={supportIcon}
            iconBuilder={termsAndConditionsSvgIcon}
            darkIconBuilder={termsAndConditionsSvgIconDark}
            onClick={() => {
              updateModalContentGetter(() => (
                <PartnerFAQs faqs={getPartnerFaqs(menuProps.clientId?.()) ?? []} />
              ));
              setIsModalOn(true);
              menuProps.setIsDropdownOpen(false);
            }}
            clientId={() => menuProps.clientId?.()!}
          />
        </Show>
        <HeaderDropdownMenuItem
          title="Help & support"
          icon="headset"
          iconUrl={supportIcon}
          iconBuilder={helpAndSupportSvgIcon}
          darkIconBuilder={helpAndSupportSvgIconDark}
          onClick={() => {
            openIntercom();
            menuProps.setIsDropdownOpen(false);
          }}
          clientId={() => menuProps.clientId?.()!}
        />
        <Show when={isLoggedIn}>
          <HeaderDropdownMenuItem
            title="Logout"
            icon="SignOut"
            iconUrl={logoutIcon}
            iconBuilder={logoutSvgIcon}
            darkIconBuilder={logoutSvgIconDark}
            onClick={handleLogout}
            clientId={() => menuProps.clientId?.()!}
          />
        </Show>
      </div>
    );
  }
}

export function HeaderDropdownMenuItem(props: {
  title: string;
  icon: string;
  iconUrl?: string;
  onClick: () => void;
  iconBuilder?: () => JSX.Element;
  darkIconBuilder?: () => JSX.Element;
  clientId?: () => string;
}) {
  return (
    <div
      class="inline-flex cursor-pointer select-none flex-row items-center justify-start self-stretch py-2 hover:bg-baseTertiaryMedium"
      onClick={props.onClick}
      classList={{
        "h-14 pl-1 pr-2 w-full gap-2 border-t border-[#313538]":
          props.clientId?.() === clientIdConfig.tdClientId,
        "pl-1 pr-3 gap-1": props.clientId?.() !== clientIdConfig.tdClientId,
      }}
    >
      <Show when={props.clientId?.() !== clientIdConfig.tdClientId}>
        <div class="h-[24px] w-[24px] items-center justify-center fill-black darkTheme:fill-white">
          <Switch>
            <Match when={props.iconBuilder || props.darkIconBuilder}>
              <div class="block darkTheme:hidden">{props.iconBuilder?.()}</div>
              <div class="hidden darkTheme:block">
                {props.darkIconBuilder?.()}
              </div>
            </Match>
            <Match when={props.iconUrl}>
              <img src={props.iconUrl} />
            </Match>
            <Match when={true}>
              <PhosphorIcon
                name={props.icon}
                size="fill"
                fontSize={18}
                class="text-basePrimaryDark darkTheme:text-white"
              />
            </Match>
          </Switch>
        </div>
      </Show>
      <div
        class="flex w-full select-none text-subtitleSmall leading-[4px] text-textDark"
        classList={{
          "text-bold": props.clientId?.() === clientIdConfig.tdClientId,
        }}
      >
        {props.title}
      </div>
      <Show when={props.clientId?.() === clientIdConfig.tdClientId}>
        <img src={rightArrowWhite} alt="" />
      </Show>
    </div>
  );
}
